import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authService } from "../services/auth";
import { useAuth } from "../context/AuthContext";
import { postAuthDestination } from "../utils/reviewerNav";
import { FaSpinner } from "../utils/icons";
import { Alert, AlertDescription, Input, Label, Button } from "@/design-system/components/react";

const SetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthState } = useAuth();
  const token = searchParams.get("token") || "";
  const emailParam = searchParams.get("email") || "";

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [email, setEmail] = useState(emailParam);
  const [userType, setUserType] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setError("No invite token provided.");
      return;
    }

    authService
      .validateInvite(token)
      .then((data) => {
        setIsValid(true);
        setEmail(emailParam);
        setUserType(data.user_type || "");
      })
      .catch((err: unknown) => {
        const detail = axios.isAxiosError(err) ? err.response?.data?.detail : null;
        setError(detail || "Invalid or expired invite link.");
      })
      .finally(() => setIsValidating(false));
  }, [token, emailParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await authService.setPassword(email, token, newPassword, confirmPassword);
      setAuthState({
        token: result.token,
        refreshToken: result.refreshToken,
        userId: result.userId,
        isAdmin: result.isAdmin,
        adminUserId: result.adminUserId ?? null,
        userType: result.userType,
        agreementAccepted: result.agreementAccepted,
      });
      // PrivateRoute bounces to /agreement when agreementAccepted === false,
      // so navigating to the user's home is enough — no inline branch here.
      navigate(
        postAuthDestination({
          userType: result.userType,
          allowedCohorts: result.allowedCohorts,
        }),
      );
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : null;
      setError(detail || "Failed to set password.");
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex min-h-screen bg-bg-2 text-white items-center justify-center">
        <FaSpinner className="animate-spin h-8 w-8 text-fg-muted" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-bg-2 text-white items-center justify-center p-4">
      <div className="w-full max-w-md bg-surface-2 backdrop-blur-md border border-border-strong rounded-lg p-8 shadow-2xl">
        <div className="text-center mb-8">
          <span className="text-2xl font-display font-bold tracking-[-0.04em] text-fg">
            a2z<span className="text-signal-text-tint">.</span>
          </span>
          <h2 className="mt-4 text-xl font-medium text-fg">Set Your Password</h2>
          {email && <p className="mt-2 text-sm text-fg-muted">{email}</p>}
          {userType && (
            <p className="mt-1 text-xs text-fg-muted capitalize">
              {userType.replace("_", " ")} account
            </p>
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6 p-3 bg-warn-tint">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isValid && (
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-fg-muted">
                  New Password
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-bg-2 border-border-strong text-white"
                  required
                  minLength={8}
                />
                <p className="text-xs text-fg-muted">
                  At least 8 characters with uppercase, lowercase, number, and special character.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-fg-muted">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-bg-2 border-border-strong text-white"
                  required
                />
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                    <span>Setting password...</span>
                  </>
                ) : (
                  <span>Set Password</span>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SetPassword;
